import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Inbox, LogOut, Store, LayoutDashboard, Building2, GaugeCircle, MailCheck, BellRing, Megaphone, Users, ShieldCheck, MapPinned, Ticket, CopyCheck, SlidersHorizontal, KanbanSquare, Star, CalendarDays, CalendarCheck, Map, CreditCard, PanelsTopLeft } from "lucide-react";
import { Logo } from "@/components/Logo";
import { EmailAlert } from "@/components/EmailAlert";

const NavItem = ({ to, icon: Icon, label, testId }) => {
  const { pathname } = useLocation();
  const active = pathname === to;
  return (
    <Link
      to={to}
      data-testid={testId}
      className={`flex items-center gap-3 px-4 py-2.5 text-sm font-semibold rounded-md transition-colors ${
        active ? "bg-brand text-white" : "text-neutral-600 hover:bg-neutral-100"
      }`}
    >
      <Icon className="h-4 w-4" />
      {label}
    </Link>
  );
};

export const Shell = ({ children, role }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const onLogout = async () => {
    await logout();
    navigate("/connexion");
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col md:flex-row">
      <aside className="md:w-64 shrink-0 border-b md:border-b-0 md:border-r border-neutral-200 bg-white md:min-h-screen">
        <div className="px-5 py-6 flex items-center justify-between md:block">
          <Link to={role === "admin" ? "/admin" : role === "qualificateur" ? "/qualif" : "/pro"} data-testid="shell-logo-link">
            <Logo className="h-14" />
          </Link>
          <p className="hidden md:block mt-1 text-xs uppercase tracking-[0.18em] text-neutral-400">
            {role === "admin" ? "Administration" : role === "qualificateur" ? "Qualification" : "Espace auto-école"}
          </p>
        </div>
        <nav className="px-3 pb-5 space-y-1">
          {role === "admin" ? (
            <>
              <NavItem to="/admin" icon={Inbox} label="Prospects" testId="nav-admin-leads" />
              <NavItem to="/admin/reservations" icon={CalendarCheck} label="Dossiers élèves" testId="nav-admin-reservations" />
              <NavItem to="/admin/commissions" icon={CreditCard} label="Commissions" testId="nav-admin-commissions" />
              <NavItem to="/admin/crm" icon={KanbanSquare} label="CRM" testId="nav-admin-crm" />
              <NavItem to="/admin/liste-attente" icon={CalendarDays} label="Liste d'attente" testId="nav-admin-waitlist" />
              <NavItem to="/admin/doublons" icon={CopyCheck} label="Doublons" testId="nav-admin-duplicates" />
              <NavItem to="/admin/rappels" icon={BellRing} label="Rappels du jour" testId="nav-admin-reminders" />
              <NavItem to="/admin/auto-ecoles" icon={Building2} label="Auto-écoles" testId="nav-admin-schools" />
              <NavItem to="/admin/partenaires" icon={ShieldCheck} label="Partenaires" testId="nav-admin-partners" />
              <NavItem to="/admin/zones" icon={MapPinned} label="Zones d'attaque" testId="nav-admin-zones" />
              <NavItem to="/admin/carte" icon={Map} label="Carte" testId="nav-admin-map" />
              <NavItem to="/admin/affiches" icon={LayoutDashboard} label="Affiches / Formules" testId="nav-admin-affiches" />
              <NavItem to="/admin/ambassadeurs" icon={Users} label="Ambassadeurs" testId="nav-admin-ambassadors" />
              <NavItem to="/admin/bon-carburant" icon={Ticket} label="Bon carburant" testId="nav-admin-bons" />
              <NavItem to="/admin/campagnes" icon={Megaphone} label="Suivi publicité" testId="nav-admin-campaigns" />
              <NavItem to="/admin/emails" icon={MailCheck} label="Journal envois" testId="nav-admin-emails" />
              <NavItem to="/admin/avis" icon={Star} label="Avis clients" testId="nav-admin-reviews" />
              <NavItem to="/admin/site" icon={PanelsTopLeft} label="Site & pop-ups" testId="nav-admin-site" />
              <NavItem to="/admin/reglages" icon={SlidersHorizontal} label="Réglages" testId="nav-admin-settings" />
            </>
          ) : role === "qualificateur" ? (
            <>
              <NavItem to="/qualif" icon={Inbox} label="Prospects à qualifier" testId="nav-qualif-inbox" />
            </>
          ) : (
            <>
              <NavItem to="/pro" icon={Inbox} label="Dossiers élèves" testId="nav-pro-reservations" />
              <NavItem to="/pro/annonces" icon={Ticket} label="Mes formules" testId="nav-pro-affiches" />
              <NavItem to="/pro/disponibilites" icon={CalendarDays} label="Mes disponibilités" testId="nav-pro-dispo" />
              <NavItem to="/pro/profil" icon={Store} label="Ma fiche" testId="nav-pro-profile" />
            </>
          )}
          <button
            onClick={onLogout}
            data-testid="logout-btn"
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-semibold text-neutral-500 hover:text-red-600 rounded-md transition-colors"
          >
            <LogOut className="h-4 w-4" />
            Déconnexion
          </button>
        </nav>
        <div className="hidden md:flex items-center gap-2 px-5 py-4 border-t border-neutral-200 text-xs text-neutral-500">
          <GaugeCircle className="h-4 w-4 text-brand" />
          <span data-testid="current-user-email">{user?.email}</span>
        </div>
      </aside>
      <main className="flex-1 min-w-0">
        {role === "admin" && <EmailAlert />}
        {children}
      </main>
    </div>
  );
};
